import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { institutionTypes, relationshipLabel } from "@alm/domain";
import type { RouterOutputs } from "@alm/api-contracts";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useFacility } from "@/lib/facility/facility";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalOneOf, searchPatch, optionalSearch } from "@/lib/search";
import { orpc, orpcClient } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import { DirectoryPage, type DirectoryColumn } from "./-components/directory-page";
import { useReferralSourceColumn } from "./-components/referral-source-column";
import { PartnerAccessCell } from "./-components/partner-access-cell";
import { facilityInput } from "./-components/directory-forms";
import { facilityValue } from "@/routes/_authenticated/_care/-components/directory-forms-shared";
import { keepWhileSame } from "@/lib/query/keep-while-same";
import {
  InstitutionForm,
  type InstitutionValues,
} from "@/routes/_authenticated/_care/-components/institution-form";

export const Route = createFileRoute("/_authenticated/_care/institutions")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    kind?: InstitutionType;
  } => ({
    q: optionalSearch(search.q),
    kind: optionalOneOf(institutionTypes, search.kind),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "institutions"),
  component: InstitutionsPage,
});

type Row = RouterOutputs["care"]["directories"]["institutions"]["list"][number];

const COLUMNS: ReadonlyArray<DirectoryColumn<Row>> = [
  {
    header: "Institution",
    cell: (row) => <span className="font-medium">{row.name}</span>,
    clamp: "md",
  },
  {
    header: "Type",
    cell: (row) => <Badge variant="outline">{relationshipLabel(row.institutionType)}</Badge>,
  },
  { header: "Phone", cell: (row) => row.phone ?? "-" },
  {
    header: "Location",
    cell: (row) => [row.city, row.state].filter(Boolean).join(", ") || "-",
    clamp: "md",
  },
  { header: "Contact", cell: (row) => row.contactName ?? "-", clamp: "md" },
  { header: "Portal", cell: (row) => <PartnerAccessCell institution={row} /> },
];

type InstitutionType = (typeof institutionTypes)[number];

function InstitutionsPage() {
  const { facilityId } = useFacility();
  const { q, kind = "all" } = Route.useSearch();
  const patch = searchPatch(useNavigate({ from: Route.fullPath }));
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(q, (next) => patch({ q: next }));
  const list = useQuery({
    ...orpc.care.directories.institutions.list.queryOptions({
      input: {
        facilityId,
        search: q || undefined,
        includeInactive: true,
        ...(kind === "all" ? {} : { institutionType: kind }),
      },
    }),
    placeholderData: keepWhileSame({ facilityId }),
  });
  const referral = useReferralSourceColumn("institution", list.data);
  const columns = referral ? [...COLUMNS, referral] : COLUMNS;
  return (
    <DirectoryPage<Row, InstitutionValues>
      noun="Institution"
      title="Institutions directory"
      meta="Hospitals, hospices and the agencies a resident is linked to."
      columns={columns}
      rows={list.data}
      isPending={list.isPending}
      error={list.error}
      search={search}
      onSearch={setSearch}
      searching={settling || list.isPlaceholderData}
      filters={
        <Select
          value={kind}
          onValueChange={(value) =>
            patch({ kind: value === "all" ? undefined : (value as InstitutionType) })
          }
        >
          <SelectTrigger className="w-[200px]" aria-label="Filter by type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {institutionTypes.map((value) => (
              <SelectItem key={value} value={value}>
                {relationshipLabel(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
      create={(values) =>
        orpcClient.care.directories.institutions.create({
          ...values,
          facilityId: facilityInput(values.facilityId),
        })
      }
      update={(row, values) =>
        orpcClient.care.directories.institutions.update({ institutionId: row.id, ...values })
      }
      remove={(row) => orpcClient.care.directories.institutions.remove({ institutionId: row.id })}
      toValues={(row) => ({ ...row, facilityId: facilityValue(row.facilityId) })}
      renderForm={(args) => <InstitutionForm {...args} />}
    />
  );
}
